import React from 'react';
import { Badge } from '@mantine/core';
import { isIsoDateTime } from '../../utils/entityPropertyUtils';
import { formatDateTime } from '../../utils/dateTimeUtils';
import type { EntityRegistryEntry, ColumnOptions } from '../types';

export const requestApprovalConfig: EntityRegistryEntry = {
    hideIdInStandard: true,
    fetchAttributes: ['Id', 'DisplayName', 'EntityType', 'Status', 'Type', 'ModificationType', 'Target', 'TargetType', 'Requestor', 'RequestorType', 'RequestCreated', 'RequestExpiration'],
    listColumns: ({ isStandard }: ColumnOptions) => {
        const columns = [
            {
                key: 'status',
                label: 'Status',
                sortable: true,
                render: (entity: any) => {
                    const status = (entity.Status || '').toString().trim();
                    if (!status) return '';
                    const colorMap: Record<string, string> = {
                        Pending: 'orange', Approved: 'green', Denied: 'red', Expired: 'gray'
                    };
                    const color = colorMap[status] ?? 'gray';
                    return <Badge variant="light" size="sm" color={color}>{status}</Badge>;
                }
            },
            {
                key: 'type',
                label: 'Type',
                sortable: true,
                render: (entity: any) => {
                    const t = (entity.Type || '').toString().trim();
                    if (!t) return '';
                    const color = t.toLowerCase() === 'possessive' ? 'violet' : 'cyan';
                    return <Badge variant="light" size="sm" color={color}>{t}</Badge>;
                }
            },
            {
                key: 'modificationtype',
                label: 'Operation',
                sortable: true,
                render: (entity: any) => {
                    const mt = (entity.ModificationType || '').toString().trim();
                    if (!mt) return '';
                    const colorMap: Record<string, string> = {
                        Create: 'green', Modify: 'blue', Delete: 'red', Replace: 'yellow'
                    };
                    const color = colorMap[mt] ?? 'gray';
                    return <Badge variant="outline" size="sm" color={color}>{mt}</Badge>;
                }
            },
            {
                key: 'target',
                label: 'Target',
                sortable: true,
                render: (entity: any) => {
                    const targetType = (entity.TargetType || '').toString().trim();
                    const targetId = (entity.Target || '').toString().trim();
                    if (!targetType && !targetId) return '';
                    const label = targetType || targetId;
                    return label.length > 25 ? `${label.slice(0, 22)}...` : label;
                }
            },
            {
                key: 'requestor',
                label: 'Requestor',
                sortable: true,
                render: (entity: any) => {
                    const reqType = (entity.RequestorType || '').toString().trim();
                    const reqId = (entity.Requestor || '').toString().trim();
                    if (!reqType && !reqId) return '';
                    const label = reqType || reqId;
                    return label.length > 25 ? `${label.slice(0, 22)}...` : label;
                }
            },
            {
                key: 'requestcreated',
                label: 'Created',
                sortable: true,
                render: (entity: any) => {
                    const val = entity.RequestCreated;
                    if (!val) return '';
                    if (typeof val === 'string' && isIsoDateTime(val)) {
                        return <span title={val}>{formatDateTime(val)}</span>;
                    }
                    return val;
                }
            }
        ];

        if (!isStandard) {
            columns.push({
                key: 'requestexpiration',
                label: 'Expires',
                sortable: true,
                render: (entity: any) => {
                    const val = entity.RequestExpiration;
                    if (!val) return '';
                    if (typeof val === 'string' && isIsoDateTime(val)) {
                        return <span title={val}>{formatDateTime(val)}</span>;
                    }
                    return val;
                }
            });
        }

        return columns;
    }
};
